import React, { useMemo } from 'react';
import { Flex, HStack, Stack, Text, VStack, Box } from '@chakra-ui/react';
import { useIsDineIn, useIsScanAndPayPage } from '@olo-web/utils/common/hooks';
import { useContactInfoState } from '@olo-web/client-state';
import { AmericanExpress } from '@olo-web/assets/icons/payments/AmericanExpress.ui';
import { DinersClub } from '@olo-web/assets/icons/payments/DinersClub.ui';
import { Discover } from '@olo-web/assets/icons/payments/Discover.ui';
import { Mastercard } from '@olo-web/assets/icons/payments/Mastercard.ui';
import { UnknownPaymentMethod } from '@olo-web/assets/icons/payments/UnknownPaymentMethod.ui';
import { Visa } from '@olo-web/assets/icons/payments/Visa.ui';
import { AddNewCard } from '@olo-web/assets/icons/payments/AddNewCard.ui';

const style = { h: 8, w: 8, mt: -1.5, mr: 1.5 };

const paymentIcon = {
  visa: <Visa {...style} />,
  mastercard: <Mastercard {...style} />,
  amex: <AmericanExpress {...style} />,
  dinersclub: <DinersClub {...style} />,
  discover: <Discover {...style} />,
};

export const CardDetailsDisplay = ({ card, accessibilityProps = {}, showEmail = true }) => {
  const isScanAndPayPage = useIsScanAndPayPage();
  const contactInfo = useContactInfoState();
  const isDineIn = useIsDineIn();
  const showGuestEmail = isScanAndPayPage && contactInfo?.email && card && showEmail;

  const icon = useMemo(() => {
    if (!card) return <AddNewCard {...style} />;
    if ('ccType' in card) {
      return paymentIcon[card.ccType.toLowerCase()];
    }
    if ('type' in card) {
      return paymentIcon[card.type];
    }
    return <UnknownPaymentMethod {...style} />;
  }, [card]);

  const lastFour = useMemo(() => {
    if (!card) return '';
    if ('ccLastFour' in card) {
      return card.ccLastFour;
    }
    if ('number' in card) {
      return card.number.slice(-4);
    }
    return '';
  }, [card]);

  const expDate = useMemo(() => {
    if (!card) return '';
    if ('ccExpiration' in card) {
      return new Date(card.ccExpiration).toLocaleString('en-US', {
        year: '2-digit',
        month: 'numeric',
      });
    }
    if ('exp' in card) {
      if (!card?.exp?.length) return '';
      return `${card.exp.slice(0, 2)}/${card.exp.slice(2, 4)}`;
    }
    return '';
  }, [card]);

  return (
    <HStack align="flex-start" spacing={3} mb={-1.5}>
      {icon}
      <VStack alignItems="flex-start">
        <Stack
          direction={`${showGuestEmail ? 'row' : 'column'}`}
          alignItems="flex-start"
          spacing={showGuestEmail ? 5 : 1}
          data-testid="saved-card"
        >
          {!card ? (
            <Text textStyle="pBold" data-testid="add-a-new-card">
              Add a new card
            </Text>
          ) : (
            <>
              {isDineIn ? (
                <>
                  <Flex {...accessibilityProps}>
                    <Text
                      textStyle="p"
                      fontSize="1rem"
                      data-testid="saved-card-last-four-and-name"
                      mr={5}
                    >
                      {lastFour}
                    </Text>
                    {expDate && (
                      <Text textStyle="p" fontSize="1rem" data-testid="saved-card-expiration">
                        {`Expires ${expDate}`}
                      </Text>
                    )}
                  </Flex>
                  {contactInfo?.email && showEmail && (
                    <Text mt="8px !important" data-testid="optional-email-address-text">
                      {contactInfo?.email}
                    </Text>
                  )}
                </>
              ) : (
                <Box {...accessibilityProps}>
                  <Text textStyle="p" fontSize="1rem" data-testid="saved-card-last-four-and-name">
                    Ending in {lastFour}
                  </Text>
                  <Text textStyle="p" fontSize="1rem" data-testid="saved-card-expiration">
                    {`Expires ${expDate}`}
                  </Text>
                </Box>
              )}
            </>
          )}
        </Stack>
        {showGuestEmail && (
          <Text textStyle="p" fontSize="1rem" data-testid="saved-card-expiration">
            {contactInfo.email}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};
